import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  axios,
  AxiosError,
  ErrorResponse,
  SitterBranchOnboardService,
  SitterBranchOnboardUrlDTO,
  SitterBranchOnboardUrlRequestDTO,
} from 'us-web-services';

import { Button, ButtonSize, ButtonType, Link } from '../../components';
import { useDependencies } from '../../dependencies';
import { AcceptingPaymentToggle } from './AcceptingPaymentToggle';
import { MembershipPayment } from './MembershipPayment';
import { PayoutFaq } from './PayoutFaq';

interface Props {
  jobId?: string;
}

export const BranchInactive = observer(({ jobId }: Props): JSX.Element => {
  const { stores } = useDependencies();
  const { sitter: { sitter } } = stores;
  const { t } = useSharedTranslation (namespaces.caregiver);

  const [branchUrl, setBranchUrl] = useState<SitterBranchOnboardUrlDTO>({} as SitterBranchOnboardUrlDTO);

  const getBranchUrl = async (): Promise<void> => {
    const userData: SitterBranchOnboardUrlRequestDTO = {
      sitterId: sitter.id ?? 0,
    };

    try {
      const response = (await SitterBranchOnboardService.create(userData)).data;
      setBranchUrl(response.data);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError<ErrorResponse, unknown>;

        if (error.response?.data.errors) {
          toast.error(error.response?.data.errors[0].message);
        }
      }
    }
  };

  useEffect(() => {
    getBranchUrl();
  }, []);

  return (
    <article className='py-6'>
      <div className='w-full'>
        <div className='flex flex-col md:flex-row'>
          <div className='md:w-1/2'>
            <h1 className='text-2xl font-semibold'>{t('pages.sitter.payout.title')}</h1>
            <div className='mb-10 border-2 rounded-2xl p-3'>
              <div className='mb-2.5 font-bold'>{t('pages.sitter.payout.active.title')}</div>
              <hr />
              <div className='block font-bold mt-2.5 mb-2.5'>{t('pages.sitter.payout.payoutHeading')}</div>
              <p className='mb-3.5'>{t('pages.sitter.payout.payoutDescriptionBranch')}</p>
              <p className='mb-3.5 font-semibold'>{t('pages.sitter.payout.payoutHeadingChoose')}</p>
              <div className='mx-2'>
                <p className='font-semibold'>{t('pages.sitter.payout.payoutHeadingWallet')}</p>
                <p>{t('pages.sitter.payout.payoutDescriptionWallet')}</p>
              </div>
              <p className='uppercase my-2'>{t('common.or')}</p>
              <div className='mx-2'>
                <p className='font-semibold'>{t('pages.sitter.payout.payoutHeadingCard')}</p>
                <p>{t('pages.sitter.payout.payoutDescriptionCard')}</p>
              </div>
              <div className='my-5'>
                {branchUrl.url && (
                  <Button buttonSize={ButtonSize.large} buttonType={ButtonType.primary} onClick={(): Window|null => window.open(branchUrl.url ?? '', '_blank', 'noreferrer')}>
                    {t('pages.sitter.payout.setupPayouts')}
                  </Button>
                )}
                {jobId && (
                  <div className='mt-5'>
                    <Link
                      className='text-sapphire font-bold'
                      to={`/job/${jobId}`}
                      title={t('pages.sitter.payout.backToJob')}
                    >
                      &laquo; {t('pages.sitter.payout.backToJob')}
                    </Link>
                  </div>
                )}
              </div>
              <div className='text-gray'>
                <FontAwesomeIcon icon={faLock} className='mr-2' />{t('pages.sitter.payout.securedBranch')}
                <p className='mt-1 text-sm italic'>{t('pages.sitter.payout.legalFootnote')}</p>
              </div>
            </div>
            <div className='md:hidden mb-10 md:p-14 md:w-1/2'>
              <PayoutFaq />
            </div>
            <div className='mb-10 border-2 rounded-2xl p-3'>
              <AcceptingPaymentToggle />
            </div>
            <MembershipPayment />
          </div>
          <div className='hidden md:block mb-10 md:p-14 md:w-1/2'>
            <PayoutFaq />
          </div>
        </div>
      </div>
    </article>
  );
});
